import type { ComponentType, FC, PropsWithChildren } from "react";
import { useRef } from "react";
import { CSSTransition } from "react-transition-group";

import { RoundButton } from "@ui/components/RoundButton";
import { useOutsideClick } from "@ui/hooks/useOutsideClick";
import { useTranslations } from "@ui/providers/useTranslations";

import { UiCrossIcon } from "@jti/brand-icons/ui-cross";

import { SearchForm } from "../SearchForm";

import styles from "./SearchHeaderBanner.module.scss";

export type SearchHeaderBannerProps = {
  isOpened: boolean;
  resultsPageUrl: string;
  Container: ComponentType<PropsWithChildren>;
  onClose: () => void;
};

export const SearchHeaderBanner: FC<SearchHeaderBannerProps> = ({ isOpened, resultsPageUrl, Container, onClose }) => {
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const { __ } = useTranslations();
  useOutsideClick(searchContainerRef, onClose, {
    excludeSelectors: ["[data-search-button]"],
  });

  return (
    <CSSTransition
      in={isOpened}
      timeout={115}
      classNames={{
        enter: styles.fadeInFromTop,
        exit: styles.fadeOutToTop,
      }}
      nodeRef={searchContainerRef}
      unmountOnExit
    >
      <div className={styles.searchContainer} data-testid="searchContainer" ref={searchContainerRef}>
        <div className={styles.searchAnimation}>
          <Container>
            <div className={styles.searchHeader}>
              <div className={styles.searchTitle}>{__("search.title")}</div>
              <RoundButton icon={UiCrossIcon} aria-label={__("common.close")} onClick={onClose} />
            </div>
            <div className={styles.searchFormWrapper}>
              <SearchForm resultsPage={resultsPageUrl} />
            </div>
          </Container>
        </div>
      </div>
    </CSSTransition>
  );
};
